import { useMemo, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { InputV2 } from "../../../Reusable/InputV2/InputV2";
import { Formik } from "formik";
import { Box, SimpleGrid, Spinner, Button, FormLabel } from "@chakra-ui/react";
import { createMetricSchema } from "../../../Pages/Organizations/AddNewOrganizationForm/schema";
import { FormErrorMessage, MetricModalFormWrapper } from "../Styles";
import { SelectV2 } from "../../../Reusable";
import {
  useAddMetricMutation,
  useUpdateMetricMutation,
  useCreateOrganizationGoalMutation,
  useEditOrganizationGoalMutation,
  useLazyGetMetricBySparkQuery,
} from "../../../../redux/reducers/apiSlice";
import {
  metricYearSelectInputOptions,
  metricMonthOrQuaterFieldOptions,
  generatePredeterminedMetricOptions,
  formatNumber,
  revertNumberOrCurrencyFormat,
  customConsole,
} from "../../../utils/helper";
import RadioButtonGroup from "../../RadioButtonGroup/RadioButtonGroup";
import { TARGET_SYMBOLS } from "../../../utils/constants";
import * as Sentry from "@sentry/react";
import { DatapointFormData } from "../../../../redux/reducers/types";
import Datapoint from "../../Datapoint";

const CreateMetricModal = ({
  goalId,
  orgId,
  sparkId,
  metric,
  onClose
}) => {
  const editMode = !!metric;
  const [addMetric, { isLoading: addMetricLoading }] = useAddMetricMutation();
  const [updateMetric, { isLoading: updateMetricLoading }] =
    useUpdateMetricMutation();
  const [
    getMetricBySpark,
    { isLoading: getMetricBySparkLoading, data: predeterminedMetrics },
  ] = useLazyGetMetricBySparkQuery();
  const [createGoal, { isLoading: createGoalLoading }] =
    useCreateOrganizationGoalMutation();
  const [editGoal, { isLoading: editGoalLoading }] =
    useEditOrganizationGoalMutation();

  const loading = createGoalLoading || editGoalLoading;

  const [disabledPredetermined, setDisabledDetermined] = useState(false);

  const fetchMetricDescription = async () => {
    try {
      const response = await getMetricBySpark(sparkId).unwrap();
      if (Array.isArray(response) && response.length === 0) {
        setDisabledDetermined(true);
      }
    } catch (e) {
      Sentry.captureException(e);
      setDisabledDetermined(true);
    }
  };

  useEffect(() => {
    fetchMetricDescription();
  }, []);

  const predeterminedMetricOptions = useMemo(() => {
    if (
      Array.isArray(predeterminedMetrics) &&
      predeterminedMetrics.length > 0
    ) {
      customConsole(
        "log",
        generatePredeterminedMetricOptions(predeterminedMetrics)
      );
      return generatePredeterminedMetricOptions(predeterminedMetrics);
    }
    return [];
  }, [predeterminedMetrics]);

  const handleSubmit = async (data) => { 
    const finalData = {};
    const startDate = `${data.year}-${data.month}`;
    const progress = {
      target: data.progress.targetValue,
      current: data.progress.current,
      targetType: data.progress.targetType,
    };

    let finalDatapoints = [];
    if(finalDatapoints.length >= 0){
      finalDatapoints = data.datapoints.map((datapoint: DatapointFormData) => ({ year: parseInt(datapoint.year), month: datapoint.month, target: datapoint.targetValue }));
    }

    
    if(data.graph_type !== 'PROGRESS_BAR' && finalDatapoints.length === 0){
      toast.error(`You should add at least 1 datapoint for ${data["graph_type"]}`);
      return;
    }

    finalData["description"] = data.description;
    finalData["startDate"] = startDate;
    finalData["progress"] = progress;
    finalData["term"] = data.term;
    finalData["graph_type"] = data.graph_type;
    finalData["datapoints"] = finalDatapoints;

    let apiCall = editMode ? updateMetric : addMetric;
    let payload = editMode
      ? {
          orgId,
          goalId,
          metricId: metric.id,
          data: finalData,
        }
      : {
          orgId,
          goalId,
          data: finalData,
        };
    try {
      //@ts-ignore
      await apiCall(payload).unwrap();

      toast.success("You just added a metric");

      onClose();
    } catch (e) {
      Sentry.captureException(e);
      toast.error(
        e?.data?.message ||
          "Something unexpected happened while trying to add your metric"
      );
    }
  };

  const getMonthFieldValueFromMetric = () => {
    let date = new Date(metric?.startDate);
    const monthIndex = date.getMonth() + 1;
    const month = monthIndex < 10 ? `0${monthIndex}` : monthIndex;
    return `${month}-01`;
  };

  if (loading) return <Spinner color="red" />;

  

  return (
    <Formik
      validationSchema={createMetricSchema}
      initialErrors={{datapoints: []}}
      initialValues={{
        predetermined: "true",
        description: metric?.description,
        progress: {
          target: metric?.progress.target
            ? formatNumber(metric.progress.target, "decimal")
            : 0,
          current: 0,
          targetType: "DOLLARS",
          targetValue: metric?.progress?.target,
          ...metric?.progress,
        },
        datapoints: [],
        graph_type: 'PROGRESS_BAR',
        term: metric?.term || "MONTHLY",
        year: metric?.startDate
          ? new Date(metric?.startDate).getFullYear()
          : metricYearSelectInputOptions()[0].value,
        month: metric?.startDate
          ? getMonthFieldValueFromMetric()
          : metricMonthOrQuaterFieldOptions("MONTHLY")[0].value,
      }}
      onSubmit={async (data) => {
        handleSubmit(data);
      }}
    >


      {(formik) => {
      const handleCreateDatapoint = () =>{
        const datapoints = formik.values.datapoints;
          const newDatapoints = [...datapoints];
          newDatapoints.push({ year: '2025', month: 'January', target: 0})
          formik.setFieldValue('datapoints', newDatapoints);
      }

      const handleRemoveDatapoint = (i) => {
        const datapoints = formik.values.datapoints; 
        const newDatapoints = [...datapoints.slice(0,i),...datapoints.slice(i+1)];
        formik.setFieldValue('datapoints', newDatapoints);
      }
        customConsole("log","\n==========", "FORMIK ERRORS: ", formik.errors, "\n\n", "FORMIK VALUES: ", formik.values, "\n==========");
        return (
          <Box
            // isOpen={isOpen}
            // onClose={onClose}
            // primaryButtonText={editMode ? "Edit metric" : "Save metric"}
            // secondaryButtonText={"Cancel"}
            // modalTitle={`${editMode ? "Edit" : "Add new"} metric`}
            // onPrimaryButtonClicked={formik.handleSubmit}
            // primaryLoading={loading}
            // disablePrimaryButton={loading}
          >
            <MetricModalFormWrapper>
              <RadioButtonGroup
                formik={formik}
                isDisabled={disabledPredetermined}
                onChange={(val) => {
                  if (val === "false") {
                    formik.setFieldValue("description", "");
                  }
                  formik.setFieldValue("predetermined", val);
                }}
                value={
                  disabledPredetermined ? "false" : formik.values.predetermined
                }
                name="predetermined"
                options={[
                  {
                    label: "Select pre-determined metric",
                    value: "true",
                    disabled: disabledPredetermined,
                  },
                  {
                    label: "Create my own",
                    value: "false",
                  },
                ]}
              />
              {formik.values.predetermined === "true" && (
                <Box mb="32px" style={{ position: "relative", zIndex: 111 }}>
                  <SelectV2
                    style={{ position: "relative" }}
                    placeholder="Select a metric"
                    name="description"
                    restSelect={{ isDisabled: disabledPredetermined }}
                    options={predeterminedMetricOptions}
                    value={formik.values.description}
                    hideError={disabledPredetermined}
                    onChange={(option) =>
                      formik.setFieldValue("description", option.value)
                    }
                  />
                </Box>
              )}
              {(disabledPredetermined ||
                formik.values.predetermined !== "true") && (
                <Box>
                  <InputV2
                    title="Add a description to your metric"
                    placeholder="Ex: I want to feed 10,000 children"
                    name="description"
                    value={formik.values.description}
                  />
                </Box>
              )}
              <Box style={{ position: "relative", zIndex: 110 }} mt={4}>
                  <SelectV2
                    title="Select the way you want to display your metric"
                    style={{ position: "relative"}}
                    placeholder="Select a graph type"
                    name="graph_type"
                    options={[{ label: "Progress bar", value: "PROGRESS_BAR"},{ label: "Line chart", value: "LINE_CHART"},{ label: "Bar chart", value: "BAR_CHART"}]}
                    value={formik.values.graph_type}
                    onChange={(option) => {
                      formik.setFieldValue("graph_type", option.value);
                      formik.setFieldValue("datapoints", []);
                    }}
                  />
                </Box>       
              <SimpleGrid
                columns={[1]}
                spacingX="44px"
              >
                <Box style={{ position: "relative", zIndex: 109 }} mt={4}>
                  <SelectV2
                    placeholder="Progress"
                    name="progress."
                    title="Choose the type of data you want to represent"
                    options={[
                      { label: "Dollars", value: "DOLLARS" },
                      { label: "Numbers", value: "NUMBERS" },
                      { label: "Percentages", value: "PERCENTAGES" },
                    ]}
                    value={formik.values.progress.targetType}
                    onChange={(option) => {
                      formik.setFieldValue("progress.targetType", option.value);
                    }}
                  />
                </Box>
                {formik.values.graph_type === "PROGRESS_BAR" && (
                
            
                <Box style={{ position: "relative", zIndex: 108}} mt={4}>
                  <InputV2
                    name="progress.target"
                    title="Set the target you want to reach"
                    restInput={{
                      onChange: (e) => {
                        const cleanedUpValue = revertNumberOrCurrencyFormat(
                          e.target.value
                        );
                        if (!isNaN(cleanedUpValue)) {
                          formik.setFieldValue(
                            "progress.targetValue",
                            cleanedUpValue
                          );
                          formik.setFieldValue(
                            "progress.target",
                            formatNumber(cleanedUpValue, "decimal")
                          );
                        } else {
                          formik.setFieldValue("progress.target", 0);
                          formik.setFieldValue("progress.targetValue", 0);
                        }
                      },
                    }}
                    placeholder="Your target"
                    value={
                      formik.values.progress.targetType === "PERCENTAGES"
                        ? `${formik.values.progress.target}${TARGET_SYMBOLS[formik.values.progress.targetType]}`
                        : `${TARGET_SYMBOLS[formik.values.progress.targetType]}${formik.values.progress.target}`
                    }
                  />
                  <FormErrorMessage>
                    {/** @ts-ignore */}
                    {formik.errors?.progress?.targetValue}
                  </FormErrorMessage>
                </Box>
                )}
              </SimpleGrid>
              {formik.values.graph_type === "PROGRESS_BAR" && (

                <Box mt={4}>
                  <FormLabel>Select year, time period and month or quarter:</FormLabel>
                  <SimpleGrid
                    columns={[3]}
                    spacingX={10}
                  >
                    <Box style={{ position: "relative", zIndex: 107 }}>
                      <SelectV2
                        name="year"
                        placeholder="Year"
                        options={metricYearSelectInputOptions()}
                        value={formik.values.year}
                        onChange={(option) =>
                          formik.setFieldValue("year", option.value)
                        }
                      />
                    </Box>
                    <Box style={{ position: "relative", zIndex: 106 }}>
                      <SelectV2
                        placeholder="Time period"
                        name="term"
                        options={[
                          { label: "Quarterly", value: "QUARTERLY" },
                          { label: "Monthly", value: "MONTHLY" },
                        ]}
                        value={formik.values.progress}
                        onChange={(option: {
                          label: string;
                          value: "QUATERLY" | "MONTHLY";
                        }) => formik.setFieldValue("term", option.value)}
                      />
                    </Box>
                    <Box style={{ position: "relative", zIndex: 105 }}>
                      <SelectV2
                        placeholder="Start of timeline"
                        name={formik.values.month}
                        onChange={(option: { label: string; value: string }) =>
                          formik.setFieldValue("month", option.value)
                        }
                        options={metricMonthOrQuaterFieldOptions(
                          formik.values.term
                        )}
                        value={formik.values.month}
                        hideError={false}
                      />
                    </Box>
                  </SimpleGrid>
                </Box>

              )}


              {formik.values.datapoints.length > 0 && (
                <div>
                  <div style={{border: "1px solid #eee", marginTop: 32}} />
                  <h2 style={{textAlign: 'center', marginTop: 16, fontWeight: 'bold'}}>Your datapoints</h2>
                  {formik.values.graph_type !== "PROGRESS_BAR" && formik.values.datapoints && Array.isArray(formik.values.datapoints) &&  formik.values.datapoints.map((datapoint,index) => (
                    <Datapoint key={index} index={index} title={`Datapoint #${index +1}`} formik={formik} handleRemoveDatapoint={() => handleRemoveDatapoint(index)}  />
                  ))}
                </div>
              )}

              {formik.values.graph_type !== "PROGRESS_BAR" && (
                <Box mt={4}>
                  <Button onClick={handleCreateDatapoint}>Add datapoint</Button>
                </Box>
              )}

              {formik.values.datapoints.length === 0 && (typeof formik.errors.datapoints === 'string') && <FormErrorMessage>
                  {formik.errors.datapoints}
                </FormErrorMessage>}
            <Box mt={8}>
                <Button onClick={formik.handleSubmit}>
                    {editMode ? 'Edit' : 'Save'} metric
                </Button>
            </Box>
            </MetricModalFormWrapper>
          </Box>
        );
      }}
    </Formik>
  );
};

export default CreateMetricModal;
